import {Flex} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classnames from 'classnames';
import React from 'react';

import IndexSelectionGroup from 'molbio/IndexSelectionGroup';
import FormGroup from 'ui-library/bs/FormGroup';
import {BLUE_POWDER_LIGHT} from 'ui-library/Colors';
import ClickableIcon, {ClickableIconStyle} from 'ui-library/icons/ClickableIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {sharedFormStyles, TranslationRegion} from './CreateEditTranslationForm';

interface TranslationPositionInputGroupProps {
  label: string;
  modelRange: TranslationRegion;
  setStart: ((value: number | null) => void) | null;
  setEnd: ((value: number | null) => void) | null;
  disabled: boolean;
  sequenceStartIndex: number;
  sequenceLength: number;
  regionLabelClassName?: string;
  onRemove?: (() => void) | null;
}

/**
 * A component with two text inputs for start and end positions of a translation or translation region
 * to be used in the form for creating and editing translations.
 */
function TranslationPositionInputGroup({
  label,
  modelRange,
  setStart,
  setEnd,
  disabled,
  sequenceStartIndex,
  sequenceLength,
  regionLabelClassName,
  onRemove,
}: TranslationPositionInputGroupProps): JSX.Element {
  return (
    <FormGroup
      className={classnames(css(sharedFormStyles.fieldRow), {
        [css(styles.selectedFieldRow)]: modelRange.selected,
      })}
    >
      <FieldLabel className={regionLabelClassName} label={label} />
      <Flex align="center" gap="xs">
        <IndexSelectionGroup
          updateEndIndex={setEnd ?? (() => {})}
          updateStartIndex={setStart ?? (() => {})}
          disabled={disabled}
          endIndex={modelRange.end}
          startIndex={modelRange.start}
          sequenceLength={sequenceLength}
          sequenceStartIndex={sequenceStartIndex}
        />
        {onRemove != null && (
          <ClickableIcon
            icon="close"
            iconStyle={ClickableIconStyle.NEGATIVE}
            onClick={onRemove}
            altText="Remove region"
          />
        )}
      </Flex>
    </FormGroup>
  );
}

export const styles = StyleSheet.create({
  selectedFieldRow: {
    backgroundColor: BLUE_POWDER_LIGHT,
    padding: '4px 12px',
    marginBottom: '11px',
  },
});

export default TranslationPositionInputGroup;
